<template>
    <div id="netgsm-extension-mapping">
        <div class="page-header">
            <div class="left">
                <h3>Dahili Eşleştirme</h3>
                <p>Telefon dahililerini Supsis kullanıcılarıyla eşleştirin</p>
            </div>
            <div class="right">
                <a-button @click="refreshData" :loading="loading" :icon="'reload'"> Yenile</a-button>
                <a-button type="primary" @click="showAddModal = true" :icon="'plus'"> Eşleştirme Ekle</a-button>
            </div>
        </div>

        <!-- Statistics Cards -->
        <div class="statistics">
            <div class="stats-grid">
                <div class="stat-card">
                    <div class="stat-icon">
                        <i class="material-icons">phone</i>
                    </div>
                    <div class="stat-content">
                        <div class="stat-value">{{ webSocketExtensions.length }}</div>
                        <div class="stat-label">WebSocket Dahili</div>
                    </div>
                </div>

                <div class="stat-card">
                    <div class="stat-icon">
                        <i class="material-icons">link</i>
                    </div>
                    <div class="stat-content">
                        <div class="stat-value">{{ Object.keys(currentMappings).length }}</div>
                        <div class="stat-label">Eşleştirilmiş</div>
                    </div>
                </div>

                <div class="stat-card">
                    <div class="stat-icon">
                        <i class="material-icons">link_off</i>
                    </div>
                    <div class="stat-content">
                        <div class="stat-value">{{ unmappedExtensions.length }}</div>
                        <div class="stat-label">Eşleştirilmemiş</div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Extension Mappings Table -->
        <div class="content">
            <SupsisTable
                :columns="getCols()"
                :rows="tableRows"
                :loading="loading"
                bottom-pagination
                :pagination-cursor="pagination"
                :options="{
                    fixedHeader: true,
                    borderX: true,
                    borderY: true,
                }"
                @cursor-changed="(cursor) => (pagination = cursor)"
            />
        </div>

        <!-- Add Mapping Modal -->
        <a-modal
            v-model="showAddModal"
            title="Dahili Eşleştirme Ekle"
            @ok="handleSaveMapping"
            @cancel="handleCancelMapping"
            :confirmLoading="saving"
            width="700px"
        >
            <a-form layout="vertical" id="frm-add-mapping">
                <a-form-item label="Dahili Seç" required>
                    <a-select
                        v-model="selectedExtension"
                        placeholder="Eşleştirilecek dahiliyi seçin"
                        style="width: 100%"
                        showSearch
                        :filterOption="filterExtension"
                    >
                        <a-select-option
                            v-for="extension in unmappedExtensions"
                            :key="extension.extension"
                            :value="extension.extension"
                        >
                            <div class="extension-option">
                                <span class="extension-number">{{ extension.extension }}</span>
                                <span class="extension-name">{{ extension.displayname }}</span>
                            </div>
                        </a-select-option>
                    </a-select>
                </a-form-item>

                <a-form-item label="Kullanıcı Seç" required>
                    <div v-if="!selectedUser" class="user-selection">
                        <a-button @click="showUserPicker = true" type="dashed" icon="user" block>
                            Kullanıcı Seç
                        </a-button>
                    </div>
                    <div v-else class="selected-user">
                        <div class="user-info">
                            <a-avatar :size="32" style="margin-right: 12px">
                                {{ getUserInitials(selectedUser) }}
                            </a-avatar>
                            <div class="user-details">
                                <div class="user-name">{{ selectedUser.name }}</div>
                                <div class="user-email">{{ selectedUser.email }}</div>
                            </div>
                            <a-button type="link" icon="close" @click="selectedUser = null" style="margin-left: auto">
                                Kaldır
                            </a-button>
                        </div>
                    </div>
                </a-form-item>
            </a-form>
        </a-modal>

        <!-- User Picker Modal -->
        <a-modal
            v-model="showUserPicker"
            title="Kullanıcı Seç"
            :footer="null"
            width="800px"
            :bodyStyle="{ padding: '15px' }"
        >
            <UserPicker @user-selected="onUserSelected" />
        </a-modal>

        <!-- Delete Confirmation Modal -->
        <a-modal
            v-model="showDeleteModal"
            title="Eşleştirme Kaldır"
            @ok="confirmDeleteMapping"
            @cancel="showDeleteModal = false"
            :confirmLoading="deleting"
        >
            <p>Bu dahili eşleştirmesini kaldırmak istediğinize emin misiniz?</p>
            <div v-if="deletingMapping" class="delete-info">
                <strong>{{ deletingMapping.extension }}</strong>
                <i class="material-icons" style="margin: 0 8px; vertical-align: middle">arrow_forward</i>
                <strong>{{ $_.get(deletingMapping, "user.name", "-") }}</strong>
            </div>
        </a-modal>
    </div>
</template>

<script>
import Vue from "vue";
import { mapActions, mapState } from "vuex";
import SupsisTable from "../../../../../components/SupsisTable.vue";
import SupsisButton from "../../../Settings/components/SupsisButton.vue";
import UserPicker from "../../../Tables/detail/pickers/UserPicker.vue";
import { WEBPHONE_NETGSM_API } from "../../../../../api/webphone.netgsm";
import log2 from "../../../../../utils/log2";

const log = log2("NetGsmExtensionMapping");

export default Vue.extend({
    name: "NetGsmExtensionMapping",
    components: {
        SupsisTable,
        SupsisButton,
        UserPicker,
    },
    props: {
        value: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            loading: false,
            saving: false,
            deleting: false,
            showAddModal: false,
            showUserPicker: false,
            showDeleteModal: false,
            extensions: [],
            selectedExtension: null,
            selectedUser: null,
            deletingMapping: null,
            pagination: {
                page: 1,
                perPage: 20,
                total: 0,
            },
        };
    },
    computed: {
        ...mapState({
            users: (state) => state.user.users?.data || [],
        }),
        currentMappings() {
            return this.value.payload?.mapping || {};
        },
        unmappedExtensions() {
            // Sadece WebSocket (wss) transport'u olan dahilileri göster
            return this.extensions.filter((ext) => 
                !this.currentMappings[ext.extension] && ext.transport === "wss"
            );
        },
        webSocketExtensions() {
            // Sadece WebSocket dahilileri - mapping için uygun olanlar
            return this.extensions.filter((ext) => ext.transport === "wss");
        },
        tableRows() {
            const mappedRows = Object.entries(this.currentMappings).map(([extensionId, userId]) => {
                const extension = this.extensions.find((ext) => ext.extension === extensionId);
                const user = this.users.find((u) => u._id === userId);

                return {
                    _id: `mapping_${extensionId}`,
                    extensionId,
                    extension,
                    user,
                    mappedAt: new Date(),
                };
            });

            // Unmapped extensions'ları da göster (sadece WebSocket olanları)
            const unmappedRows = this.unmappedExtensions.map((ext) => ({
                _id: `unmapped_${ext.extension}`,
                extensionId: ext.extension,
                extension: ext,
                user: null,
                mappedAt: null,
            }));

            const allRows = [...mappedRows, ...unmappedRows];
            this.pagination.total = allRows.length;

            return allRows;
        },
    },
    methods: {
        ...mapActions({
            updateChannel: "channel/updateChannel",
            fetchUsers: "user/getUsers",
        }),
        getCols() {
            return [
                {
                    key: "extension",
                    field: "extension",
                    title: "Dahili",
                    width: 120,
                    align: "left",
                    renderBodyCell: ({ row }) => {
                        if (!row.extension) return <span>-</span>;
                        return (
                            <div class="extension-info">
                                <div class="extension-number">{row.extension.extension}</div>
                                <div class="extension-name">{row.extension.displayname}</div>
                            </div>
                        );
                    },
                },
                {
                    key: "user",
                    field: "user",
                    title: "Eşleştirilen Kullanıcı",
                    width: 250,
                    align: "left",
                    renderBodyCell: ({ row }) => {
                        if (!row.user) {
                            return (
                                <div class="no-mapping">
                                    <i class="material-icons" style="color: #ccc; margin-right: 8px;">
                                        person_off
                                    </i>
                                    <span style="color: #999;">Eşleştirilmemiş</span>
                                </div>
                            );
                        }

                        return (
                            <div class="user-info">
                                <a-avatar size={28} style="margin-right: 10px;">
                                    {this.getUserInitials(row.user)}
                                </a-avatar>
                                <div class="user-details">
                                    <div class="user-name">{row.user.name}</div>
                                    <div class="user-email">{row.user.email}</div>
                                </div>
                            </div>
                        );
                    },
                },
                {
                    title: "İşlemler",
                    key: "actions",
                    width: 120,
                    align: "center",
                    renderBodyCell: ({ row }) => {
                        if (!row.user) {
                            // Unmapped extension - show add button
                            return (
                                <SupsisButton
                                    onClick={() => this.addMappingForExtension(row.extension)}
                                    small
                                    content="Kullanıcı Ata"
                                    v-tippy={{ followCursor: true, placement: "bottom-end" }}
                                >
                                    <i class="material-icons text-primary">person_add</i>
                                </SupsisButton>
                            );
                        } else {
                            // Mapped extension - show remove button
                            return (
                                <SupsisButton
                                    onClick={() => this.deleteMapping(row)}
                                    small
                                    content="Eşleştirmeyi Kaldır"
                                    v-tippy={{ followCursor: true, placement: "bottom-end" }}
                                >
                                    <i class="material-icons text-danger">link_off</i>
                                </SupsisButton>
                            );
                        }
                    },
                },
            ];
        },
        async loadExtensions() {
            try {
                this.loading = true;
                const response = await WEBPHONE_NETGSM_API.extensions.list();

                if (response && response.extensions) {
                    this.extensions = response.extensions;
                    log("NetGSM extensions loaded:", this.extensions.length);
                } else {
                    this.extensions = [];
                }
            } catch (error) {
                console.error("Extensions yüklenirken hata:", error);
                this.$message.error("Dahili listesi yüklenirken hata oluştu");
                this.extensions = [];
            } finally {
                this.loading = false;
            }
        },
        async refreshData() {
            await this.loadExtensions();
        },
        addMappingForExtension(extension) {
            this.selectedExtension = extension.extension;
            this.selectedUser = null;
            this.showAddModal = true;
        },
        onUserSelected(user) {
            this.selectedUser = user;
            this.showUserPicker = false;
        },
        async handleSaveMapping() {
            if (!this.selectedExtension || !this.selectedUser) {
                this.$message.error("Dahili ve kullanıcı seçimi zorunludur");
                return;
            }

            try {
                this.saving = true;

                // Update the mapping
                const newMappings = {
                    ...this.currentMappings,
                    [this.selectedExtension]: this.selectedUser._id,
                };

                await this.saveMapping(newMappings);
                this.$message.success("Dahili eşleştirmesi başarıyla eklendi");
                this.handleCancelMapping();
            } catch (error) {
                console.error("Mapping kaydedilirken hata:", error);
                this.$message.error("Eşleştirme kaydedilirken hata oluştu");
            } finally {
                this.saving = false;
            }
        },
        handleCancelMapping() {
            this.showAddModal = false;
            this.selectedExtension = null;
            this.selectedUser = null;
        },
        deleteMapping(mappingRow) {
            this.deletingMapping = mappingRow;
            this.showDeleteModal = true;
        },
        async confirmDeleteMapping() {
            if (!this.deletingMapping) return;

            try {
                this.deleting = true;

                const newMappings = { ...this.currentMappings };
                delete newMappings[this.deletingMapping.extensionId];

                await this.saveMapping(newMappings);
                this.$message.success("Dahili eşleştirmesi başarıyla kaldırıldı");

                this.showDeleteModal = false;
                this.deletingMapping = null;
            } catch (error) {
                console.error("Mapping silinirken hata:", error);
                this.$message.error("Eşleştirme kaldırılırken hata oluştu");
            } finally {
                this.deleting = false;
            }
        },
        async saveMapping(mappings) {
            const updatedPayload = {
                ...this.value.payload,
                mapping: mappings,
            };

            const updatedChannel = {
                ...this.value,
                payload: updatedPayload,
            };

            // Update channel via API
            await this.updateChannel({
                _id: this.value._id,
                name: this.value.name,
                enabled: this.value.enabled,
                payload: updatedPayload,
                visibility: this.value.visibility,
                forward: this.value.forward,
                commentForward: this.value.commentForward,
                zohoForward: this.value.zohoForward,
            });

            // Emit the updated channel
            this.$emit("input", updatedChannel);
        },
        getUserInitials(user) {
            if (!user || !user.name) return "?";
            return user.name
                .split(" ")
                .map((n) => n[0])
                .join("")
                .toUpperCase()
                .substring(0, 2);
        },
        filterExtension(input, option) {
            const extension = this.unmappedExtensions.find((ext) => ext.extension === option.key);
            if (!extension) return false;
            return (extension.extension + " " + extension.displayname).toLowerCase().includes(input.toLowerCase());
        },
    },
    async mounted() {
        // Load users first
        await this.fetchUsers({
            perPage: 1000,
            page: 1,
            query: "",
            updateStore: true,
        });

        // Then load extensions
        await this.loadExtensions();
    },
});
</script>

<style lang="scss">
#netgsm-extension-mapping {
    padding: 20px;
    background: var(--color-bg-default);
    height: 100%;

    .page-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 20px;
        padding-bottom: 15px;
        border-bottom: 1px solid var(--color-border-default);

        .left {
            h3 {
                margin: 0 0 5px 0;
                color: var(--color-text-primary);
                font-size: 20px;
                font-weight: 600;
            }

            p {
                margin: 0;
                color: var(--color-text-secondary);
                font-size: 14px;
            }
        }

        .right {
            display: flex;
            gap: 10px;
        }
    }

    .statistics {
        margin-bottom: 20px;

        .stats-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 20px;

            .stat-card {
                background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
                border-radius: 12px;
                padding: 20px;
                display: flex;
                align-items: center;
                gap: 16px;
                box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
                border: 1px solid #e2e8f0;
                transition: all 0.3s ease;

                &:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
                }

                .stat-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    background: linear-gradient(135deg, #3b82f6, #1d4ed8);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    flex-shrink: 0;

                    .material-icons {
                        font-size: 24px;
                    }
                }

                .stat-content {
                    .stat-value {
                        font-size: 24px;
                        font-weight: 700;
                        color: #1d4ed8;
                        line-height: 1.2;
                        margin-bottom: 4px;
                    }

                    .stat-label {
                        font-size: 13px;
                        font-weight: 500;
                        color: #64748b;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                    }
                }
            }
        }
    }

    .content {
        background: var(--color-bg-container);
        border-radius: 8px;
        border: 1px solid var(--color-border-default);
        overflow: hidden;
    }

    .extension-info {
        .extension-number {
            font-weight: 600;
            color: var(--color-text-primary);
        }

        .extension-name {
            font-size: 12px;
            color: var(--color-text-secondary);
        }
    }

    .user-info {
        display: flex;
        align-items: center;

        .user-details {
            .user-name {
                font-weight: 500;
                color: var(--color-text-primary);
                font-size: 14px;
            }

            .user-email {
                font-size: 12px;
                color: var(--color-text-secondary);
            }
        }
    }

    .no-mapping {
        display: flex;
        align-items: center;
        font-style: italic;
    }

    .extension-option {
        display: flex;
        align-items: center;
        gap: 8px;

        .extension-number {
            font-weight: 600;
            color: var(--color-text-primary);
        }

        .extension-name {
            color: var(--color-text-secondary);
            flex: 1;
        }
    }

    .user-selection {
        .ant-btn {
            height: 50px;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            font-size: 14px;
        }
    }

    .selected-user {
        .user-info {
            padding: 12px;
            background: #f0f9ff;
            border: 1px solid #0ea5e9;
            border-radius: 8px;
        }
    }

    .delete-info {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 16px;
        background: #fef2f2;
        border: 1px solid #fecaca;
        border-radius: 8px;
        margin-top: 12px;

        i {
            color: #6b7280;
        }
    }
}
</style>
